import {
  OBSIDIAN_BLOCK_ID,
  QUARTZ_BLOCK_ID,
  NETHER_PORTAL_BLOCK_ID,
  END_PORTAL_BLOCK_ID,
  FIRE_BLOCK_ID,
  PORTAL_INNER_WIDTH,
  PORTAL_INNER_HEIGHT,
  NETHER_PORTAL_MAX_INNER_WIDTH,
  NETHER_PORTAL_MAX_INNER_HEIGHT,
  NETHER_RATIO
} from '../config/game-constants.js';
import { clamp } from '../utils/math-helpers.js';

const NETHER_IGNITION_INTERIOR_ALLOW_IDS = new Set([FIRE_BLOCK_ID]);
const PORTAL_IGNITION_NEIGHBOR_OFFSETS = [
  [0, 0, 0],
  [1, 0, 0],
  [-1, 0, 0],
  [0, 0, 1],
  [0, 0, -1],
  [0, 1, 0],
  [0, -1, 0]
];

function clampPortalWidth(value, fallback = PORTAL_INNER_WIDTH) {
  return clamp(
    Math.floor(Number(value) || fallback),
    PORTAL_INNER_WIDTH,
    NETHER_PORTAL_MAX_INNER_WIDTH
  );
}

function clampPortalHeight(value, fallback = PORTAL_INNER_HEIGHT) {
  return clamp(
    Math.floor(Number(value) || fallback),
    PORTAL_INNER_HEIGHT,
    NETHER_PORTAL_MAX_INNER_HEIGHT
  );
}

function normalizePortalCell(x, y, z) {
  return {
    x: Math.round(Number(x) || 0),
    y: Math.round(Number(y) || 0),
    z: Math.round(Number(z) || 0)
  };
}

function toPortalCellKey(x, y, z) {
  return `${x},${y},${z}`;
}

function toPortalFrameKey(frame) {
  if (!frame) return '';
  const width = clampPortalWidth(frame?.width, PORTAL_INNER_WIDTH);
  const height = clampPortalHeight(frame?.height, PORTAL_INNER_HEIGHT);
  if (frame.axis === 'x') {
    return `x:${Math.round(frame.left)}:${Math.round(frame.baseY)}:${Math.round(frame.z)}:${width}:${height}`;
  }
  return `z:${Math.round(frame.front)}:${Math.round(frame.x)}:${Math.round(frame.baseY)}:${width}:${height}`;
}

export function createPortalSystem({
  world,
  ui,
  player,
  controls,
  sounds,
  isCreativeMode,
  showStatusByKey,
  refreshBgmContext,
  stopMining,
  cancelBowCharge,
  stopShieldBlocking,
  findSafeRespawnNear,
  t
} = {}) {
  const translate = typeof t === 'function' ? t : (key) => key;

  function resolvePortalSearchBounds(opts = {}) {
    const minWidth = clampPortalWidth(opts.minWidth, PORTAL_INNER_WIDTH);
    const maxWidth = clampPortalWidth(opts.maxWidth, minWidth);
    const minHeight = clampPortalHeight(opts.minHeight, PORTAL_INNER_HEIGHT);
    const maxHeight = clampPortalHeight(opts.maxHeight, minHeight);

    return {
      minWidth,
      maxWidth: Math.max(minWidth, maxWidth),
      minHeight,
      maxHeight: Math.max(minHeight, maxHeight)
    };
  }

  function getNetherPortalSearchBounds() {
    return resolvePortalSearchBounds({
      minWidth: PORTAL_INNER_WIDTH,
      maxWidth: NETHER_PORTAL_MAX_INNER_WIDTH,
      minHeight: PORTAL_INNER_HEIGHT,
      maxHeight: NETHER_PORTAL_MAX_INNER_HEIGHT
    });
  }

  function getPortalInnerWidth(frame) {
    return clampPortalWidth(frame?.width, PORTAL_INNER_WIDTH);
  }

  function getPortalInnerHeight(frame) {
    return clampPortalHeight(frame?.height, PORTAL_INNER_HEIGHT);
  }

  function makePortalFrameAxisX(left, baseY, z, width = PORTAL_INNER_WIDTH, height = PORTAL_INNER_HEIGHT) {
    return {
      axis: 'x',
      left: Math.round(left),
      baseY: Math.round(baseY),
      z: Math.round(z),
      width: clampPortalWidth(width),
      height: clampPortalHeight(height)
    };
  }

  function makePortalFrameAxisZ(front, x, baseY, width = PORTAL_INNER_WIDTH, height = PORTAL_INNER_HEIGHT) {
    return {
      axis: 'z',
      front: Math.round(front),
      x: Math.round(x),
      baseY: Math.round(baseY),
      width: clampPortalWidth(width),
      height: clampPortalHeight(height)
    };
  }

  function forEachPortalInteriorCell(frame, fn) {
    const minY = frame.baseY + 1;
    const width = getPortalInnerWidth(frame);
    const height = getPortalInnerHeight(frame);
    const maxY = frame.baseY + height;
    if (frame.axis === 'x') {
      for (let y = minY; y <= maxY; y++) {
        for (let x = frame.left; x < frame.left + width; x++) fn(x, y, frame.z);
      }
      return;
    }

    for (let y = minY; y <= maxY; y++) {
      for (let z = frame.front; z < frame.front + width; z++) fn(frame.x, y, z);
    }
  }

  function forEachPortalBoundaryCell(frame, fn) {
    const bottomY = frame.baseY;
    const width = getPortalInnerWidth(frame);
    const height = getPortalInnerHeight(frame);
    const topY = frame.baseY + height + 1;
    if (frame.axis === 'x') {
      const minX = frame.left - 1;
      const maxX = frame.left + width;
      for (let x = minX; x <= maxX; x++) {
        fn(x, bottomY, frame.z);
        fn(x, topY, frame.z);
      }
      for (let y = frame.baseY + 1; y <= frame.baseY + height; y++) {
        fn(minX, y, frame.z);
        fn(maxX, y, frame.z);
      }
      return;
    }

    const minZ = frame.front - 1;
    const maxZ = frame.front + width;
    for (let z = minZ; z <= maxZ; z++) {
      fn(frame.x, bottomY, z);
      fn(frame.x, topY, z);
    }
    for (let y = frame.baseY + 1; y <= frame.baseY + height; y++) {
      fn(frame.x, y, minZ);
      fn(frame.x, y, maxZ);
    }
  }

  function isPortalInteriorCell(frame, x, y, z) {
    const minY = frame.baseY + 1;
    const width = getPortalInnerWidth(frame);
    const height = getPortalInnerHeight(frame);
    const maxY = frame.baseY + height;
    if (y < minY || y > maxY) return false;
    if (frame.axis === 'x') {
      return z === frame.z && x >= frame.left && x < frame.left + width;
    }
    return x === frame.x && z >= frame.front && z < frame.front + width;
  }

  function isAnchorOnPortalBoundary(frame, x, y, z) {
    let hit = false;
    forEachPortalBoundaryCell(frame, (bx, by, bz) => {
      if (bx === x && by === y && bz === z) hit = true;
    });
    return hit;
  }

  function isPortalFrameCorner(frame, x, y, z) {
    const bottomY = frame.baseY;
    const width = getPortalInnerWidth(frame);
    const height = getPortalInnerHeight(frame);
    const topY = frame.baseY + height + 1;
    if (y !== bottomY && y !== topY) return false;

    if (frame.axis === 'x') {
      if (z !== frame.z) return false;
      const minX = frame.left - 1;
      const maxX = frame.left + width;
      return x === minX || x === maxX;
    }

    if (x !== frame.x) return false;
    const minZ = frame.front - 1;
    const maxZ = frame.front + width;
    return z === minZ || z === maxZ;
  }

  function isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, opts = {}) {
    const allowPortalInside = opts.allowPortalInside !== false;
    const allowMissingCorners = opts.allowMissingCorners === true;
    const allowedInteriorBlockIds = opts.allowedInteriorBlockIds instanceof Set
      ? opts.allowedInteriorBlockIds
      : null;

    let valid = true;
    forEachPortalBoundaryCell(frame, (x, y, z) => {
      if (!valid) return;
      if (world.getBlockIdAt(x, y, z) === frameBlockId) return;
      if (allowMissingCorners && isPortalFrameCorner(frame, x, y, z)) return;
      valid = false;
    });
    if (!valid) return false;

    forEachPortalInteriorCell(frame, (x, y, z) => {
      if (!valid) return;
      const id = world.getBlockIdAt(x, y, z);
      if (id === null) return;
      if (allowPortalInside && id === portalBlockId) return;
      if (allowedInteriorBlockIds && allowedInteriorBlockIds.has(id)) return;
      valid = false;
    });
    return valid;
  }

  function createPortalFrameValidationCache(frameBlockId, portalBlockId, validateOpts = {}) {
    const cache = new Map();
    return (frame) => {
      const key = toPortalFrameKey(frame);
      if (cache.has(key)) return cache.get(key);
      const valid = isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, validateOpts);
      cache.set(key, valid);
      return valid;
    };
  }

  function isAllowedPortalInteriorId(id, portalBlockId, opts = {}) {
    if (id === null) return true;
    if (opts.allowPortalInside !== false && id === portalBlockId) return true;
    const allowedInteriorBlockIds = opts.allowedInteriorBlockIds instanceof Set
      ? opts.allowedInteriorBlockIds
      : null;
    return !!allowedInteriorBlockIds?.has(id);
  }

  function tryTracePortalFrameAxisXFromInterior(
    x,
    y,
    z,
    frameBlockId,
    portalBlockId,
    searchOpts = {},
    validateOpts = {},
    validateFrame = null
  ) {
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(searchOpts);
    const checkFrame = typeof validateFrame === 'function'
      ? validateFrame
      : (frame) => isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, validateOpts);

    let interiorBottomY = y;
    while (
      (y - interiorBottomY) < (maxHeight - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(x, interiorBottomY - 1, z), portalBlockId, validateOpts)
    ) {
      interiorBottomY -= 1;
    }

    const baseY = interiorBottomY - 1;
    if (world.getBlockIdAt(x, baseY, z) !== frameBlockId) return null;

    let left = x;
    while (
      (x - left) < (maxWidth - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(left - 1, interiorBottomY, z), portalBlockId, validateOpts)
    ) {
      left -= 1;
    }
    const leftBoundary = left - 1;
    if (world.getBlockIdAt(leftBoundary, interiorBottomY, z) !== frameBlockId) return null;

    let right = x;
    while (
      (right - x) < (maxWidth - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(right + 1, interiorBottomY, z), portalBlockId, validateOpts)
    ) {
      right += 1;
    }
    const rightBoundary = right + 1;
    if (world.getBlockIdAt(rightBoundary, interiorBottomY, z) !== frameBlockId) return null;

    const width = (right - left) + 1;
    if (width < minWidth || width > maxWidth) return null;

    let height = 0;
    while (height < maxHeight) {
      const rowY = interiorBottomY + height;
      let rowValid = true;
      for (let ix = left; ix <= right; ix++) {
        if (!isAllowedPortalInteriorId(world.getBlockIdAt(ix, rowY, z), portalBlockId, validateOpts)) {
          rowValid = false;
          break;
        }
      }
      if (!rowValid) break;
      height += 1;
    }
    if (height < minHeight) return null;

    const frame = makePortalFrameAxisX(left, baseY, z, width, height);
    return checkFrame(frame) ? frame : null;
  }

  function tryTracePortalFrameAxisZFromInterior(
    x,
    y,
    z,
    frameBlockId,
    portalBlockId,
    searchOpts = {},
    validateOpts = {},
    validateFrame = null
  ) {
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(searchOpts);
    const checkFrame = typeof validateFrame === 'function'
      ? validateFrame
      : (frame) => isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, validateOpts);

    let interiorBottomY = y;
    while (
      (y - interiorBottomY) < (maxHeight - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(x, interiorBottomY - 1, z), portalBlockId, validateOpts)
    ) {
      interiorBottomY -= 1;
    }

    const baseY = interiorBottomY - 1;
    if (world.getBlockIdAt(x, baseY, z) !== frameBlockId) return null;

    let front = z;
    while (
      (z - front) < (maxWidth - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(x, interiorBottomY, front - 1), portalBlockId, validateOpts)
    ) {
      front -= 1;
    }
    const frontBoundary = front - 1;
    if (world.getBlockIdAt(x, interiorBottomY, frontBoundary) !== frameBlockId) return null;

    let back = z;
    while (
      (back - z) < (maxWidth - 1)
      && isAllowedPortalInteriorId(world.getBlockIdAt(x, interiorBottomY, back + 1), portalBlockId, validateOpts)
    ) {
      back += 1;
    }
    const backBoundary = back + 1;
    if (world.getBlockIdAt(x, interiorBottomY, backBoundary) !== frameBlockId) return null;

    const width = (back - front) + 1;
    if (width < minWidth || width > maxWidth) return null;

    let height = 0;
    while (height < maxHeight) {
      const rowY = interiorBottomY + height;
      let rowValid = true;
      for (let iz = front; iz <= back; iz++) {
        if (!isAllowedPortalInteriorId(world.getBlockIdAt(x, rowY, iz), portalBlockId, validateOpts)) {
          rowValid = false;
          break;
        }
      }
      if (!rowValid) break;
      height += 1;
    }
    if (height < minHeight) return null;

    const frame = makePortalFrameAxisZ(front, x, baseY, width, height);
    return checkFrame(frame) ? frame : null;
  }

  function findIgnitablePortalFrameFromInteriorCell(
    x,
    y,
    z,
    frameBlockId,
    portalBlockId,
    searchOpts = {},
    validateOpts = {},
    validateFrame = null
  ) {
    const frameX = tryTracePortalFrameAxisXFromInterior(
      x,
      y,
      z,
      frameBlockId,
      portalBlockId,
      searchOpts,
      validateOpts,
      validateFrame
    );
    if (frameX) return frameX;

    return tryTracePortalFrameAxisZFromInterior(
      x,
      y,
      z,
      frameBlockId,
      portalBlockId,
      searchOpts,
      validateOpts,
      validateFrame
    );
  }

  function clearBlockForced(x, y, z) {
    const existing = world.getBlockAt(x, y, z);
    if (!existing) return;
    world.removeBlock(existing, { trackModification: true });
  }

  function setBlockForced(x, y, z, blockId) {
    const existing = world.getBlockAt(x, y, z);
    if (existing && existing.userData?.id === blockId) return existing;
    if (existing) world.removeBlock(existing, { trackModification: true });
    return world.createBlock(x, y, z, blockId);
  }

  function setPortalInteriorState(frame, portalBlockId, enabled = true) {
    forEachPortalInteriorCell(frame, (x, y, z) => {
      const id = world.getBlockIdAt(x, y, z);
      if (enabled) {
        if (id === portalBlockId) return;
        if (id !== null) clearBlockForced(x, y, z);
        world.createBlock(x, y, z, portalBlockId);
        return;
      }

      if (id === portalBlockId) clearBlockForced(x, y, z);
    });
  }

  function findIgnitablePortalFrameFromBlock(
    x,
    y,
    z,
    frameBlockId,
    portalBlockId,
    searchOpts = {},
    validateOpts = {},
    validateFrame = null
  ) {
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(searchOpts);
    const checkFrame = typeof validateFrame === 'function'
      ? validateFrame
      : (frame) => isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, validateOpts);
    for (let height = maxHeight; height >= minHeight; height--) {
      for (let width = maxWidth; width >= minWidth; width--) {
        for (let baseY = y - (height + 1); baseY <= y; baseY++) {
          for (let left = x - width; left <= x + 1; left++) {
            const frameX = makePortalFrameAxisX(left, baseY, z, width, height);
            if (!isAnchorOnPortalBoundary(frameX, x, y, z)) continue;
            if (checkFrame(frameX)) return frameX;
          }

          for (let front = z - width; front <= z + 1; front++) {
            const frameZ = makePortalFrameAxisZ(front, x, baseY, width, height);
            if (!isAnchorOnPortalBoundary(frameZ, x, y, z)) continue;
            if (checkFrame(frameZ)) return frameZ;
          }
        }
      }
    }
    return null;
  }

  function findIgnitablePortalFrameFromCell(
    x,
    y,
    z,
    frameBlockId,
    portalBlockId,
    searchOpts = {},
    validateOpts = {},
    validateFrame = null
  ) {
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(searchOpts);
    const checkFrame = typeof validateFrame === 'function'
      ? validateFrame
      : (frame) => isStandardPortalFrameValid(frame, frameBlockId, portalBlockId, validateOpts);
    for (let height = maxHeight; height >= minHeight; height--) {
      for (let width = maxWidth; width >= minWidth; width--) {
        for (let baseY = y - (height + 1); baseY <= y; baseY++) {
          for (let left = x - width; left <= x + 1; left++) {
            const frameX = makePortalFrameAxisX(left, baseY, z, width, height);
            if (!isPortalInteriorCell(frameX, x, y, z) && !isAnchorOnPortalBoundary(frameX, x, y, z)) continue;
            if (checkFrame(frameX)) return frameX;
          }
          for (let front = z - width; front <= z + 1; front++) {
            const frameZ = makePortalFrameAxisZ(front, x, baseY, width, height);
            if (!isPortalInteriorCell(frameZ, x, y, z) && !isAnchorOnPortalBoundary(frameZ, x, y, z)) continue;
            if (checkFrame(frameZ)) return frameZ;
          }
        }
      }
    }
    return null;
  }

  function findPortalFrameContainingBlock(x, y, z, frameBlockId, portalBlockId, opts = {}) {
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(opts);
    const validateOpts = {
      allowPortalInside: true,
      allowMissingCorners: true
    };
    for (let height = maxHeight; height >= minHeight; height--) {
      for (let width = maxWidth; width >= minWidth; width--) {
        for (let baseY = y - height; baseY <= y - 1; baseY++) {
          for (let left = x - (width - 1); left <= x; left++) {
            const frameX = makePortalFrameAxisX(left, baseY, z, width, height);
            if (!isPortalInteriorCell(frameX, x, y, z)) continue;
            if (isStandardPortalFrameValid(frameX, frameBlockId, portalBlockId, validateOpts)) return frameX;
          }
          for (let front = z - (width - 1); front <= z; front++) {
            const frameZ = makePortalFrameAxisZ(front, x, baseY, width, height);
            if (!isPortalInteriorCell(frameZ, x, y, z)) continue;
            if (isStandardPortalFrameValid(frameZ, frameBlockId, portalBlockId, validateOpts)) return frameZ;
          }
        }
      }
    }
    return null;
  }

  function cleanupBrokenPortalBlocksAround(x, y, z, portalBlockId, frameBlockId, radius = 3, opts = {}) {
    const r = Math.max(1, Math.floor(radius));
    const { minWidth, maxWidth, minHeight, maxHeight } = resolvePortalSearchBounds(opts);
    const horizontalRadius = Math.max(r, maxWidth + 1);
    const verticalRadius = Math.max(PORTAL_INNER_HEIGHT + 1, maxHeight + 1);
    for (let bx = x - horizontalRadius; bx <= x + horizontalRadius; bx++) {
      for (let by = y - verticalRadius; by <= y + verticalRadius; by++) {
        for (let bz = z - horizontalRadius; bz <= z + horizontalRadius; bz++) {
          if (world.getBlockIdAt(bx, by, bz) !== portalBlockId) continue;
          if (findPortalFrameContainingBlock(bx, by, bz, frameBlockId, portalBlockId, {
            minWidth,
            maxWidth,
            minHeight,
            maxHeight
          })) continue;
          clearBlockForced(bx, by, bz);
        }
      }
    }
  }

  function hasUsableFlintSteel() {
    return ui.getActiveItemId() === 'flint_steel' && ui.getActiveItemCount() > 0;
  }

  function collectIgnitionCandidates(seedCells = []) {
    const out = [];
    const seen = new Set();

    for (const seed of seedCells) {
      if (!seed) continue;
      const center = normalizePortalCell(seed.x, seed.y, seed.z);
      for (const [ox, oy, oz] of PORTAL_IGNITION_NEIGHBOR_OFFSETS) {
        const cell = normalizePortalCell(center.x + ox, center.y + oy, center.z + oz);
        const key = toPortalCellKey(cell.x, cell.y, cell.z);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(cell);
      }
    }

    return out;
  }

  function findIgnitablePortalFrameFromCandidateCells(candidates, frameBlockId, portalBlockId, searchOpts, validateOpts) {
    const allowedInteriorBlockIds = validateOpts?.allowedInteriorBlockIds instanceof Set
      ? validateOpts.allowedInteriorBlockIds
      : null;
    const allowPortalInside = validateOpts?.allowPortalInside !== false;
    const validateFrame = createPortalFrameValidationCache(frameBlockId, portalBlockId, validateOpts);
    const interiorCandidates = [];
    const interiorCandidateKeys = new Set();
    const boundaryCandidates = [];
    const pushInteriorCandidate = (cell) => {
      if (!cell) return;
      const key = toPortalCellKey(cell.x, cell.y, cell.z);
      if (interiorCandidateKeys.has(key)) return;
      interiorCandidateKeys.add(key);
      interiorCandidates.push(cell);
    };

    for (const cell of candidates) {
      const id = world.getBlockIdAt(cell.x, cell.y, cell.z);
      if (id === frameBlockId) {
        boundaryCandidates.push(cell);
        continue;
      }
      if (id === null || (allowPortalInside && id === portalBlockId) || (allowedInteriorBlockIds && allowedInteriorBlockIds.has(id))) {
        pushInteriorCandidate(cell);
      }
    }

    for (const cell of boundaryCandidates) {
      for (const [ox, oy, oz] of PORTAL_IGNITION_NEIGHBOR_OFFSETS) {
        if (ox === 0 && oy === 0 && oz === 0) continue;
        const neighbor = normalizePortalCell(cell.x + ox, cell.y + oy, cell.z + oz);
        const id = world.getBlockIdAt(neighbor.x, neighbor.y, neighbor.z);
        if (id === null || (allowPortalInside && id === portalBlockId) || (allowedInteriorBlockIds && allowedInteriorBlockIds.has(id))) {
          pushInteriorCandidate(neighbor);
        }
      }
    }

    for (const cell of interiorCandidates) {
      const tracedFrame = findIgnitablePortalFrameFromInteriorCell(
        cell.x,
        cell.y,
        cell.z,
        frameBlockId,
        portalBlockId,
        searchOpts,
        validateOpts,
        validateFrame
      );
      if (tracedFrame) return tracedFrame;
    }

    return null;
  }

  function tryIgnitePortalWithSeedCells(seedCells = []) {
    if (!hasUsableFlintSteel()) return false;
    const candidates = collectIgnitionCandidates(seedCells);
    if (candidates.length <= 0) return false;

    const frame = findIgnitablePortalFrameFromCandidateCells(
      candidates,
      OBSIDIAN_BLOCK_ID,
      NETHER_PORTAL_BLOCK_ID,
      getNetherPortalSearchBounds(),
      {
        allowPortalInside: true,
        allowMissingCorners: true,
        allowedInteriorBlockIds: NETHER_IGNITION_INTERIOR_ALLOW_IDS
      }
    );
    if (!frame) return false;

    setPortalInteriorState(frame, NETHER_PORTAL_BLOCK_ID, true);
    if (!isCreativeMode()) ui.damageActiveTool(1);
    sounds.placeByBlockId(OBSIDIAN_BLOCK_ID);
    showStatusByKey?.('status.portal_activated');
    return true;
  }

  function getAdjacentCellFromHit(hit) {
    if (!hit || !hit.object || !hit.face || !hit.face.normal) return null;
    const pos = hit.object.position;
    return normalizePortalCell(
      pos.x + hit.face.normal.x,
      pos.y + hit.face.normal.y,
      pos.z + hit.face.normal.z
    );
  }

  function tryIgnitePortalFromHitFace(hit) {
    if (!hasUsableFlintSteel()) return false;
    const seeds = [];
    const adjacent = getAdjacentCellFromHit(hit);
    if (adjacent) seeds.push(adjacent);
    if (hit?.object?.position) {
      seeds.push(normalizePortalCell(hit.object.position.x, hit.object.position.y, hit.object.position.z));
    }
    return tryIgnitePortalWithSeedCells(seeds);
  }

  function tryIgnitePortalFromObsidianBlock(block) {
    if (!block || block.userData?.id !== OBSIDIAN_BLOCK_ID) return false;
    return tryIgnitePortalWithSeedCells([
      normalizePortalCell(block.position.x, block.position.y, block.position.z)
    ]);
  }

  function tryIgniteFireFromHitFace(hit) {
    if (!hasUsableFlintSteel()) return false;
    const cell = getAdjacentCellFromHit(hit);
    if (!cell) return false;
    if (!world.igniteAt(cell.x, cell.y, cell.z)) return false;
    if (!isCreativeMode()) ui.damageActiveTool(1);
    sounds.placeByBlockId(FIRE_BLOCK_ID);
    return true;
  }

  function tryActivateEndPortalFromQuartzBlock(block) {
    if (!block || block.userData?.id !== QUARTZ_BLOCK_ID) return false;
    if (ui.getActiveItemId() !== 'ender_eye' || ui.getActiveItemCount() <= 0) return false;
    if (world.getDimensionAt(block.position.x, block.position.z) !== 'overworld') return false;

    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    const frame = findIgnitablePortalFrameFromBlock(
      x,
      y,
      z,
      QUARTZ_BLOCK_ID,
      END_PORTAL_BLOCK_ID,
      undefined,
      {
        allowPortalInside: true,
        allowMissingCorners: true
      }
    );
    if (!frame) return false;

    setPortalInteriorState(frame, END_PORTAL_BLOCK_ID, true);
    if (!isCreativeMode()) ui.removeItem('ender_eye', 1);
    sounds.placeByBlockId(QUARTZ_BLOCK_ID);
    showStatusByKey?.('status.end_portal_activated');
    return true;
  }

  function buildPortalAt(targetX, baseY, targetZ, axis = 'x', frameBlockId = OBSIDIAN_BLOCK_ID, portalBlockId = NETHER_PORTAL_BLOCK_ID) {
    const bx = Math.round(targetX);
    const by = Math.round(baseY);
    const bz = Math.round(targetZ);
    const frame = axis === 'z'
      ? makePortalFrameAxisZ(bz - 1, bx, by)
      : makePortalFrameAxisX(bx - 1, by, bz);

    forEachPortalBoundaryCell(frame, (x, y, z) => {
      setBlockForced(x, y, z, frameBlockId);
    });
    setPortalInteriorState(frame, portalBlockId, true);
    return frame;
  }

  function findPortalBuildSpotNear(targetX, targetZ, targetDimension) {
    const tx = Math.round(targetX);
    const tz = Math.round(targetZ);
    for (let radius = 0; radius <= 5; radius++) {
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const x = tx + dx;
          const z = tz + dz;
          if (world.getDimensionAt(x, z) !== targetDimension) continue;
          const y = Math.round(world.getSurfaceHeight(x, z));
          if (!world.isSolidAt(x, y, z)) continue;
          return { x, y: y + 1, z };
        }
      }
    }

    const fallbackY = Math.round(world.getSurfaceHeight(tx, tz)) + 1;
    return { x: tx, y: fallbackY, z: tz };
  }

  function findExistingPortalBlockNear(targetX, targetZ, targetDimension, portalBlockId, radius = 24) {
    const tx = Math.round(targetX);
    const tz = Math.round(targetZ);
    const minY = -12;
    const maxY = 72;
    let best = null;
    let bestDistSq = Number.POSITIVE_INFINITY;

    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const x = tx + dx;
        const z = tz + dz;
        if (world.getDimensionAt(x, z) !== targetDimension) continue;
        const distSqXZ = dx * dx + dz * dz;
        if (distSqXZ > radius * radius) continue;

        for (let y = maxY; y >= minY; y--) {
          if (world.getBlockIdAt(x, y, z) !== portalBlockId) continue;
          const distSq = distSqXZ + (y * 0.1) * (y * 0.1);
          if (distSq < bestDistSq) {
            bestDistSq = distSq;
            best = { x, y, z };
          }
        }
      }
    }

    return best;
  }

  function getPortalSpawnPosition(frame) {
    const standHeight = Math.max(1.8, player.getHeight());
    if (frame.axis === 'x') {
      return { x: frame.left + 0.5, y: frame.baseY + standHeight + 0.52, z: frame.z };
    }
    return { x: frame.x, y: frame.baseY + standHeight + 0.52, z: frame.front + 0.5 };
  }

  function findPortalArrivalNear(targetX, targetZ, targetDimension, portalBlockId, frameBlockId) {
    const existing = findExistingPortalBlockNear(targetX, targetZ, targetDimension, portalBlockId, 26);
    if (existing) {
      const safe = typeof findSafeRespawnNear === 'function'
        ? findSafeRespawnNear(existing.x, existing.y, existing.z)
        : null;
      if (safe && Number.isFinite(safe.x) && Number.isFinite(safe.y) && Number.isFinite(safe.z)) {
        return { spawnPos: safe, reused: true };
      }
      const standHeight = Math.max(1.8, player.getHeight());
      return {
        spawnPos: { x: existing.x, y: existing.y + standHeight + 0.52, z: existing.z },
        reused: true
      };
    }

    const spot = findPortalBuildSpotNear(targetX, targetZ, targetDimension);
    const frame = buildPortalAt(spot.x, spot.y, spot.z, 'x', frameBlockId, portalBlockId);
    return {
      spawnPos: getPortalSpawnPosition(frame),
      reused: false
    };
  }

  function getPlayerPortalBlockId() {
    const p = player.getPosition();
    const feetY = player.getFeetY();
    const sampleXZ = [
      [0, 0],
      [0.28, 0],
      [-0.28, 0],
      [0, 0.28],
      [0, -0.28]
    ];
    const sampleY = [0.1, 1.0, 1.8];

    for (const [dx, dz] of sampleXZ) {
      for (const dy of sampleY) {
        const id = world.getBlockIdAt(p.x + dx, feetY + dy, p.z + dz);
        if (id === NETHER_PORTAL_BLOCK_ID || id === END_PORTAL_BLOCK_ID) return id;
      }
    }
    return null;
  }

  function tryTravelThroughNetherPortal() {
    const pos = player.getPosition().clone();
    const fromDimension = world.getDimensionAt(pos.x, pos.z);
    if (fromDimension !== 'overworld' && fromDimension !== 'nether') return false;
    const targetDimension = fromDimension === 'nether' ? 'overworld' : 'nether';

    const localX = world.toLocalX(Math.round(pos.x), fromDimension);
    const localZ = Math.round(pos.z);
    const ratio = fromDimension === 'overworld' ? (1 / NETHER_RATIO) : NETHER_RATIO;
    const targetLocalX = Math.round(localX * ratio);
    const targetLocalZ = Math.round(localZ * ratio);
    const targetWorldX = world.toDimensionX(targetLocalX, targetDimension);
    const targetWorldZ = targetLocalZ;

    world.generateTerrain({ x: targetWorldX, z: targetWorldZ });
    const arrival = findPortalArrivalNear(
      targetWorldX,
      targetWorldZ,
      targetDimension,
      NETHER_PORTAL_BLOCK_ID,
      OBSIDIAN_BLOCK_ID
    );
    const spawnPos = arrival.spawnPos;

    controls.getObject().position.copy(spawnPos);
    player.resetInput();
    stopMining?.();
    cancelBowCharge?.();
    stopShieldBlocking?.();
    world.generateTerrain({ x: spawnPos.x, z: spawnPos.z });
    refreshBgmContext?.(true, targetDimension);

    showStatusByKey?.('status.portal_travel', { dimension: translate(`dimension.${targetDimension}`) }, 'level', 2.8);
    return true;
  }

  function tryTravelThroughEndPortal() {
    const pos = player.getPosition().clone();
    const fromDimension = world.getDimensionAt(pos.x, pos.z);
    const targetDimension = fromDimension === 'end' ? 'overworld' : 'end';

    const localX = world.toLocalX(Math.round(pos.x), fromDimension);
    const localZ = Math.round(pos.z);
    const ratio = fromDimension === 'end' ? 2 : 0.5;
    const targetLocalX = Math.round(localX * ratio);
    const targetLocalZ = Math.round(localZ * ratio);
    const targetWorldX = world.toDimensionX(targetLocalX, targetDimension);
    const targetWorldZ = targetLocalZ;

    world.generateTerrain({ x: targetWorldX, z: targetWorldZ });
    const arrival = findPortalArrivalNear(
      targetWorldX,
      targetWorldZ,
      targetDimension,
      END_PORTAL_BLOCK_ID,
      QUARTZ_BLOCK_ID
    );
    const spawnPos = arrival.spawnPos;

    controls.getObject().position.copy(spawnPos);
    player.resetInput();
    stopMining?.();
    cancelBowCharge?.();
    stopShieldBlocking?.();
    world.generateTerrain({ x: spawnPos.x, z: spawnPos.z });
    refreshBgmContext?.(true, targetDimension);

    showStatusByKey?.('status.portal_travel', { dimension: translate(`dimension.${targetDimension}`) }, 'level', 2.8);
    return true;
  }

  function cleanupBrokenPortalBlocksNear(x, y, z) {
    const bx = Math.round(Number(x) || 0);
    const by = Math.round(Number(y) || 0);
    const bz = Math.round(Number(z) || 0);
    cleanupBrokenPortalBlocksAround(
      bx,
      by,
      bz,
      NETHER_PORTAL_BLOCK_ID,
      OBSIDIAN_BLOCK_ID,
      3,
      getNetherPortalSearchBounds()
    );
    cleanupBrokenPortalBlocksAround(
      bx,
      by,
      bz,
      END_PORTAL_BLOCK_ID,
      QUARTZ_BLOCK_ID,
      3
    );
  }

  return {
    constants: {
      obsidianBlockId: OBSIDIAN_BLOCK_ID,
      quartzBlockId: QUARTZ_BLOCK_ID,
      netherPortalBlockId: NETHER_PORTAL_BLOCK_ID,
      endPortalBlockId: END_PORTAL_BLOCK_ID
    },
    tryIgnitePortalFromHitFace,
    tryIgnitePortalFromObsidianBlock,
    tryIgniteFireFromHitFace,
    tryActivateEndPortalFromQuartzBlock,
    cleanupBrokenPortalBlocksNear,
    getPlayerPortalBlockId,
    tryTravelThroughNetherPortal,
    tryTravelThroughEndPortal
  };
}
